import React from "react";
import { connect } from "react-redux";
import SidebarResizer from "../sideList/SidebarResizer";
import ReactResizeDetector from "react-resize-detector";
import {
  ViewPortRoot,
  TimelineContent,
  ViewPortContainer,
  TimelineRoot,
  ViewPortTitleArea,
  ViewPortMainArea,
  TasksRoot,
} from "../common/StyledComponents";
import { getGridColumns } from "../../../backend/helpers/configurationHelpers";
import { ensureStartAndEnd } from "../../../backend/helpers/dateHelpers";
import TimeLineCommon from "./TimeLineCommon";
import VerticalResizer from "./VerticalResizer";
import TaskList from "../sideList/TaskList";
import SpinnerWrapper from "../common/SpinnerWrapper";
import { RESIZER_THICKNESS, TIMELINE_MIN_HEIGHT, TASK_MIN_WIDTH } from "../../helpers/Const";
import FilterPanel from "../filters/FilterPanel";
import DataTask from "../viewport/DataTask";
import { differenceInMinutes, startOfDay } from "date-fns";
import ViewportBackground from "../viewport/ViewportBackground";
import { iRootState } from "src/rematch/store";
import { IHierarchyItem } from "src/backend/interfaces/IItem";

class PropertyTimeLine extends TimeLineCommon {

  keyIndex: number;

  renderTasks = (rowInfo: {start: number, end: number}, visibleData: IHierarchyItem[], dragUnderway: boolean) => {
    let result = [];
    this.keyIndex = 0;
    for (let i = rowInfo.start; i < rowInfo.end; i++) {
      const task = visibleData[i];
      if (task.hierarchyLevel === 0) {
        // Root level tasks should have all their child rows recursively on the same row
        const children = task.children.map((childId) => this.props.allData[childId]).filter((child) => child != null);
        result = result.concat(this.renderTaskRow(children, i));
      } else {
        const objectTypeConfig        = this.props.getObjectTypeConfig(task.objectType);
        const [validStart, validEnd] = ensureStartAndEnd(task.start, task.end, startOfDay(Date.now()), task.duration, objectTypeConfig.durationUnit);
        const left = Math.round(differenceInMinutes(validStart, this.props.startDate) / 60 * this.props.hourWidth);
        const width = Math.round(differenceInMinutes(validEnd, validStart) / 60 * this.props.hourWidth);
        const minWidthAdjustment = Math.max(TASK_MIN_WIDTH - width, 0);
        result.push(
          <DataTask
            key={this.keyIndex++}
            index={i}
            task={task}
            /*label={task.name}*/
            color={task.color}
            left={left}
            width={width}
            minWidthAdjustment={minWidthAdjustment}
            dragUnderway={dragUnderway}
            zoomMode={this.props.zoomMode}
          ></DataTask>
        );
      }
    }
    return result;
  };

  renderTaskRow = (taskList: IHierarchyItem[], rowIndex: number, dragUnderway?: boolean) => {
    const result = [];
    for (const task of taskList) {

      const objectTypeConfig = this.props.getObjectTypeConfig(task.objectType);

      const [validStart, validEnd] = ensureStartAndEnd(task.start, task.end, startOfDay(Date.now()), task.duration, objectTypeConfig.durationUnit);
      const position = Math.round(differenceInMinutes(validStart, this.props.startDate) / 60 * this.props.hourWidth);
      const width = Math.round(differenceInMinutes(validEnd, validStart) / 60 * this.props.hourWidth);

      const minWidthAdjustment = Math.max(TASK_MIN_WIDTH - width, 0);
      result.push(
        <DataTask
          key={this.keyIndex++}
          index={rowIndex}
          task={task}
          /*label={task.name}*/
          color={task.color}
          left={position}
          width={width}
          minWidthAdjustment={minWidthAdjustment}
          hourWidth={this.props.hourWidth}
          dragUnderway={dragUnderway}
          zoomMode={this.props.zoomMode}
        />
      );
    }
    return result;
  };

  render() {
    const { visibleData, dragUnderway, rowInfo, taskListExtraHeight } = this.getCommonRenderVars();

    const taskListProps = {
      taskHeight: this.props.taskHeight,
      visibleData: visibleData,
      dragUnderway: dragUnderway,
      onScroll: this.handleVerticalScroll,
      onListVerticalSizeUpdate: this.onListVerticalSizeUpdate,
      scrollTop: this.state.scrollTop,
      width: this.props.sideBarWidth,
      startRow: rowInfo.start,
      endRow: rowInfo.end,
      taskListExtraHeight: taskListExtraHeight,
      onToggleTaskExpand: this.toggleTaskExpand,
      onToggleExpandAll: this.toggleExpandAll,
      expandedTasks: this.state.expandedTasks,
      //onApplyTaskOnProperty: this.props.onApplyTaskOnProperty,
      onAddSortColumn: this.addSortColumn,
      onColumnResize: this.onColumnResize,
      visibleTaskColumns: this.state.visibleTaskColumns,
      sortOrder: this.state.sortOrder,
      viewIndex: this.props.viewIndex,
      viewType: this.props.viewType,
    };

    let timelineHeight = this.props.timelineTotalHeight ? this.props.timelineTotalHeight : TIMELINE_MIN_HEIGHT;
    if (!this.props.isLast) {
      timelineHeight -= RESIZER_THICKNESS;
    }

    return (
      <TimelineRoot className="timeline-root">
        <TimelineContent>
          <TaskList {...taskListProps} />
          <SidebarResizer onTaskListSizing={this.props.onTaskListSizing} />
          <ViewPortMainArea height={timelineHeight}>
            <ViewPortTitleArea>
              <FilterPanel viewIndex={this.props.viewIndex} />
            </ViewPortTitleArea>
            <ViewPortRoot id={this.resizeRef} ref={this.viewPortRoot} onScroll={this.handleScroll}>
              <ReactResizeDetector handleWidth={true} handleHeight={true} onResize={this.onResize} querySelector={`#${this.resizeRef}`}></ReactResizeDetector>
              {this.props.headerElem}
              {this.props.loading ? (
                <SpinnerWrapper />
              ) : (
                <ViewPortContainer className="viewport-container" height={visibleData.length * this.props.taskHeight}>
                  <ViewportBackground
                    zoomMode={this.props.zoomMode}
                    hourWidth={this.props.hourWidth}
                    taskCount={visibleData.length}
                    startDate={this.props.startDate}
                    endDate={this.props.endDate}
                    dragUnderway={dragUnderway}
                  />
                  <TasksRoot width={this.props.chartTotalWidth} height={visibleData.length * this.props.taskHeight}>
                    {this.renderTasks(rowInfo, visibleData, dragUnderway)}
                  </TasksRoot>
                </ViewPortContainer>
              )}
            </ViewPortRoot>
          </ViewPortMainArea>
        </TimelineContent>
        {this.props.isLast ? null : (
          <VerticalResizer onTimelineVerticalResizerDrag={this.props.onTimelineVerticalResizerDrag} viewIndex={this.props.viewIndex} />
        )}
      </TimelineRoot>
    );
  }
}

const mapState = (state: iRootState) => ({
  taskHeight: state.UI.displaySettings.taskHeight,
  visibleColumns: getGridColumns(state.common.configuration, state.UI.displaySettings),
  getObjectTypeConfig: (objectTypeId: number) => Array.from(state.common.configuration.objectTypeConfigurations.values()).find(i => i.objectTypeId === objectTypeId),
});

export default connect(mapState, null)(PropertyTimeLine);
